import { Fragment } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';

import { ColumnDef, CellContext, createColumnHelper, Row, Table } from '@tanstack/react-table';

import {
  GiftOutline,
  Individual,
  IndividualStatus,
  TickCircleBold,
  InviteOutline as Invite,
  MoreCircleOutline as MoreCircle,
  CloseCircleOutline as CloseCircle,
  EditOutline as Edit,
  TrashOutline as Trash,
} from '@aslanpay/portal-common-lib';

import { AdminActions, AdminBulkActions } from 'app/components/UI/Table/Table.types';

import { Status } from 'app/constants/individual';

const giftingAdminActionsValues = [{ id: 'invite', value: 'Invite to Aslan', icon: Invite }];

const GiftingAdminColumns = {
  firstName: 'First name',
  lastName: 'Last name',
  phoneNumber: 'Mobile number',
  email: 'Company email',
  status: 'Admin status',
  canTopUpCompanyPoints: 'Buy points permission',
} as const;

export function getGiftingAdminsColumns(
  actions: AdminActions,
  disableCanTopUpPoints: boolean,
  admin?: Individual
) {
  const columnHelper = createColumnHelper<Individual>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<Individual, any>[] = Object.keys(GiftingAdminColumns).map((field) => {
    return columnHelper.accessor(field as keyof Individual, {
      cell: (info) => {
        switch (field as keyof Individual) {
          case 'email':
            return <Text color="brand.purple">{info.getValue() as string}</Text>;
          case 'status':
            return (
              <Badge colorScheme={`badge.${Status[info.getValue() as IndividualStatus]?.color}`}>
                {Status[info.getValue() as IndividualStatus]?.value}
              </Badge>
            );
          case 'canTopUpCompanyPoints':
            return (
              <Flex align="center" gap="5px">
                {info.getValue() ? (
                  <TickCircleBold boxSize="16px" color="system.success" />
                ) : (
                  <CloseCircle boxSize="16px" color="system.error" />
                )}
                <Text>{info.getValue() ? 'Yes' : 'No'}</Text>
              </Flex>
            );
          case 'phoneNumber':
            return <Text>{(info.getValue() as string) || '-'}</Text>;
          default:
            return info.getValue();
        }
      },

      header: GiftingAdminColumns[field as keyof typeof GiftingAdminColumns],
      enableSorting: true,
    });
  });

  columns.unshift({
    id: 'select',
    enableSorting: false,
    header: ({ table }: { table: Table<Individual> }) => (
      <Checkbox
        {...{
          isChecked: table.getIsAllPageRowsSelected(),
          isIndeterminate: table.getIsSomePageRowsSelected(),
          onChange: table.getToggleAllPageRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }: { row: Row<Individual> }) => (
      <Checkbox
        {...{
          isChecked: row.getIsSelected(),
          isIndeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      customStyles: { justifyContent: 'center' },
      pin: 'left',
      withBackground: true,
    },
  });

  columns.push(
    columnHelper.display({
      id: 'options',
      meta: {
        getCellContext: (context: CellContext<Individual, unknown>) => {
          if (context.column.id === 'options') return { style: { padding: 0, textAlign: 'end' } };
          return undefined;
        },
        customStyles: { justifyContent: 'center' },
        pin: 'right',
      },
      cell: (info) => (
        <Menu autoSelect={false} strategy="fixed">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<MoreCircle boxSize="20px" />}
            variant="menu"
          />
          <Portal>
            <MenuList>
              {Object.keys(actions).flatMap((action, index) => {
                const onActionClick = actions[action as keyof typeof actions];
                if (onActionClick) {
                  let actionLabel = '';
                  let actionIcon = null;
                  let isDisabled = false;

                  if (action === 'invite') {
                    actionLabel = 'Invite to Aslan';
                    actionIcon = <Invite boxSize="14px" />;

                    const { status } = info.row.original;
                    if (status !== 'CREATED' && status !== 'INVITED') {
                      isDisabled = true;
                    }
                  } else if (
                    action === 'toggleBuyPoints' &&
                    info.row.getValue('canTopUpCompanyPoints')
                  ) {
                    actionLabel = 'Remove permission to buy points';
                    actionIcon = <GiftOutline boxSize="14px" />;

                    if (!admin?.canTopUpCompanyPoints || disableCanTopUpPoints) {
                      isDisabled = true;
                    }
                  } else if (
                    action === 'toggleBuyPoints' &&
                    !info.row.getValue('canTopUpCompanyPoints')
                  ) {
                    actionLabel = 'Give permission to buy points';
                    actionIcon = <GiftOutline boxSize="14px" />;

                    if (!admin?.canTopUpCompanyPoints) {
                      isDisabled = true;
                    }
                  } else if (action === 'editDetails') {
                    actionLabel = 'Edit admin details';
                    actionIcon = <Edit boxSize="14px" />;
                  } else if (action === 'delete') {
                    actionLabel = 'Delete admin';
                    actionIcon = <Trash boxSize="14px" color="system.error" />;
                  }
                  return (
                    <Fragment key={action}>
                      {index > 0 && <MenuDivider key={`${info.row.index}${action}`} />}
                      <MenuItem
                        key={action}
                        color="brand.black"
                        onClick={() => onActionClick([info.row.index])}
                        icon={actionIcon ?? undefined}
                        isDisabled={isDisabled}
                      >
                        {actionLabel}
                      </MenuItem>
                    </Fragment>
                  );
                }
                return [];
              })}
            </MenuList>
          </Portal>
        </Menu>
      ),
    })
  );

  return columns;
}

export function getGiftingAdminBulkActionsValues<TData extends object>(
  actions: AdminBulkActions,
  selectedRows: Row<TData>[]
) {
  const isInviteDisabled = !selectedRows.some((row) =>
    ['CREATED'].includes((row.original as Individual).status)
  );

  const bulkActions = {
    ...actions,
    invite: { ...actions.invite, isDisabled: isInviteDisabled },
  };

  const bulkActionsElements = Object.keys(bulkActions).map((bulkAction) => {
    const actionValue = giftingAdminActionsValues.find((value) => value.id === bulkAction);

    return (
      <Button
        key={bulkAction}
        size="bulk"
        variant="bulk"
        onClick={() =>
          bulkActions[bulkAction as keyof AdminBulkActions].action(
            selectedRows.map((row) => row.index)
          )
        }
        isDisabled={bulkActions[bulkAction as keyof AdminBulkActions].isDisabled}
      >
        <Icon as={actionValue?.icon} boxSize="20px" />
        <Text>{actionValue?.value}</Text>
      </Button>
    );
  });

  return { label: 'admin', bulkActions: bulkActionsElements };
}

export default getGiftingAdminsColumns;
